import React, { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectAssignableRobots } from '../../store/general/general-slice-selectors';
import { getAssignableRobots } from '../../store/general/general-actions';
import AssignableRobot from '../../models/general/versions/assignable-robot';
import TorizonVersionSet from '../../models/general/versions/torizon-version-set';

/** Filter value standing for "no set assigned" -- an empty string can't collide with a uuid. */
const UNASSIGNED = '';

const TorizonRobotAssignmentTable: React.FC<{
  versionSets: TorizonVersionSet[];
  selectedRobotIds: string[];
  onSelectionChange: (ids: string[]) => void;
  selectedSetId: string | null;
}> = ({ versionSets, selectedRobotIds, onSelectionChange, selectedSetId }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const robots = useAppSelector((state) => selectAssignableRobots(state));

  const [models, setModels] = useState<string[]>([]);
  const [generations, setGenerations] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [setIds, setSetIds] = useState<string[]>([]);

  useEffect(() => {
    if (robots.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getAssignableRobots());
    }
  }, [dispatch, robots.rest.getStatus]);

  const robotList = useMemo(() => robots.data ?? [], [robots.data]);

  const distinct = (values: string[]) => Array.from(new Set(values)).sort();

  // An empty filter means "no restriction" rather than "nothing matches" -- that is what
  // makes select-all read as "everything currently shown".
  const filteredRobots = useMemo(
    () =>
      robotList.filter(
        (robot) =>
          (models.length === 0 || models.includes(robot.model)) &&
          (generations.length === 0 || generations.includes(robot.robotGeneration)) &&
          (countries.length === 0 || countries.includes(robot.country)) &&
          (tags.length === 0 || robot.tags.some((tag) => tags.includes(tag))) &&
          (setIds.length === 0 || setIds.includes(robot.versionSetId ?? UNASSIGNED)),
      ),
    [robotList, models, generations, countries, tags, setIds],
  );

  const multiSelectChangeHandler =
    (setValue: (value: string[]) => void) => (event: SelectChangeEvent<string[]>) => {
      const { value } = event.target;
      setValue(typeof value === 'string' ? value.split(',') : value);
    };

  const isAlreadyOnSelectedSet = (robot: AssignableRobot) =>
    selectedSetId != null && robot.versionSetId === selectedSetId;

  const toggleRobotHandler = (robot: AssignableRobot) => {
    if (isAlreadyOnSelectedSet(robot)) return;
    onSelectionChange(
      selectedRobotIds.includes(robot.id)
        ? selectedRobotIds.filter((id) => id !== robot.id)
        : [...selectedRobotIds, robot.id],
    );
  };

  // Robots already on the selected set aren't selectable -- their checkbox is locked.
  const selectableRobots = filteredRobots.filter((robot) => !isAlreadyOnSelectedSet(robot));

  const filteredRobotIds = useMemo(
    () => new Set(filteredRobots.map((robot) => robot.id)),
    [filteredRobots],
  );

  // Selected robots the active filters are currently hiding -- they stay selected, but the
  // user can no longer see them in the table, so they need calling out separately.
  const hiddenSelectedRobots = useMemo(
    () =>
      robotList.filter(
        (robot) => selectedRobotIds.includes(robot.id) && !filteredRobotIds.has(robot.id),
      ),
    [robotList, filteredRobotIds, selectedRobotIds],
  );

  const allSelectableSelected =
    selectableRobots.length > 0 &&
    selectableRobots.every((robot) => selectedRobotIds.includes(robot.id));

  const toggleSelectAllClickHandler = () => {
    onSelectionChange(allSelectableSelected ? [] : selectableRobots.map((robot) => robot.id));
  };

  const invertSelectionClickHandler = () => {
    // Like select-all/unselect-all, this only ever acts on the visible rows -- what you
    // could actually be clicking.
    onSelectionChange(
      selectableRobots
        .filter((robot) => !selectedRobotIds.includes(robot.id))
        .map((robot) => robot.id),
    );
  };

  const unselectHiddenClickHandler = () => {
    const hiddenIds = new Set(hiddenSelectedRobots.map((robot) => robot.id));
    onSelectionChange(selectedRobotIds.filter((id) => !hiddenIds.has(id)));
  };

  const getVersionSetName = (versionSetId: string | null) =>
    versionSets.find((vs) => vs.id === versionSetId)?.name ?? t('info-not-set');

  const renderFilter = (
    testId: string,
    label: string,
    value: string[],
    setValue: (value: string[]) => void,
    options: { value: string; label: string }[],
  ) => (
    <FormControl size="small" sx={{ minWidth: 160 }}>
      <InputLabel id={`${testId}-label`}>{label}</InputLabel>
      <Select
        multiple
        labelId={`${testId}-label`}
        label={label}
        value={value}
        onChange={multiSelectChangeHandler(setValue)}
        renderValue={(selected) =>
          options
            .filter((option) => selected.includes(option.value))
            .map((option) => option.label)
            .join(', ')
        }
        inputProps={{ 'data-testid': testId }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={value.includes(option.value)} />
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {renderFilter(
          'filter-torizon-model',
          t('label-torizon-filter-model'),
          models,
          setModels,
          distinct(robotList.map((robot) => robot.model)).map((value) => ({
            value,
            label: value,
          })),
        )}
        {renderFilter(
          'filter-torizon-generation',
          t('label-torizon-filter-generation'),
          generations,
          setGenerations,
          distinct(robotList.map((robot) => robot.robotGeneration)).map((value) => ({
            value,
            label: value,
          })),
        )}
        {renderFilter(
          'filter-torizon-country',
          t('label-torizon-filter-country'),
          countries,
          setCountries,
          distinct(robotList.map((robot) => robot.country)).map((value) => ({
            value,
            label: value,
          })),
        )}
        {renderFilter(
          'filter-torizon-tag',
          t('label-torizon-filter-tag'),
          tags,
          setTags,
          distinct(robotList.flatMap((robot) => robot.tags)).map((value) => ({
            value,
            label: value,
          })),
        )}
        {renderFilter(
          'filter-torizon-version-set',
          t('label-torizon-filter-version-set'),
          setIds,
          setSetIds,
          [
            { value: UNASSIGNED, label: t('info-not-set') },
            ...versionSets.map((vs) => ({ value: vs.id, label: vs.name })),
          ],
        )}
      </Stack>

      {hiddenSelectedRobots.length > 0 && (
        <CustomAlert
          severity="warning"
          data-testid="alert-torizon-hidden-selected-robots"
          header={t('warning-torizon-hidden-selected-robots', {
            count: hiddenSelectedRobots.length,
          })}
          action={
            <Button
              color="inherit"
              size="small"
              data-testid="button-unselect-hidden-robots"
              onClick={unselectHiddenClickHandler}
            >
              {t('button-unselect-hidden-robots')}
            </Button>
          }
        />
      )}

      <Stack direction="row" spacing={2} alignItems="center">
        <Button data-testid="button-toggle-select-all-robots" onClick={toggleSelectAllClickHandler}>
          {allSelectableSelected ? t('button-deselect-all') : t('button-select-all')}
        </Button>
        <Button data-testid="button-invert-selection-robots" onClick={invertSelectionClickHandler}>
          {t('button-invert-selection-robots')}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {t('label-torizon-version-set-robots', { count: selectedRobotIds.length })}
        </Typography>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>{t('table-header-serial-number')}</TableCell>
              <TableCell>{t('table-header-name')}</TableCell>
              <TableCell>{t('label-torizon-filter-model')}</TableCell>
              <TableCell>{t('label-torizon-filter-generation')}</TableCell>
              <TableCell>{t('label-torizon-filter-country')}</TableCell>
              <TableCell>{t('table-header-organization')}</TableCell>
              <TableCell>{t('label-torizon-filter-version-set')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRobots.map((robot) => (
              <TableRow key={robot.id} data-testid="torizon-robot-row" hover>
                <TableCell padding="checkbox">
                  <Tooltip
                    title={
                      isAlreadyOnSelectedSet(robot)
                        ? t('info-tooltip-torizon-robot-already-assigned')
                        : ''
                    }
                  >
                    <span data-testid={`checkbox-wrapper-${robot.id}`}>
                      <Checkbox
                        checked={
                          isAlreadyOnSelectedSet(robot) || selectedRobotIds.includes(robot.id)
                        }
                        disabled={isAlreadyOnSelectedSet(robot)}
                        onChange={() => toggleRobotHandler(robot)}
                        inputProps={
                          {
                            'data-testid': `checkbox-robot-${robot.id}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>{robot.serialNumber}</TableCell>
                <TableCell>{robot.name}</TableCell>
                <TableCell>{robot.model}</TableCell>
                <TableCell>{robot.robotGeneration}</TableCell>
                <TableCell>{robot.country}</TableCell>
                <TableCell>{robot.organizationName}</TableCell>
                <TableCell>{getVersionSetName(robot.versionSetId)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default TorizonRobotAssignmentTable;
